import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../api';

import ProfileHeader from '../components/learner/ProfileHeader';
import ProfileActions from '../components/learner/ProfileActions';
import TrainingSection from '../components/learner/TrainingSection';
import PhishingHistory from '../components/learner/PhishingHistory';
import ComplianceSection from '../components/learner/ComplianceSection';
import RiskScoreChart from '../components/learner/RiskScoreChart';
import AdminNotes from '../components/learner/AdminNotes';

const { FiArrowLeft, FiLoader } = FiIcons;

const getArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      if (val.includes(',')) return val.split(',').map(s => s.trim());
      return [val];
    }
  }
  return [val];
};

const LearnerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [learner, setLearner] = useState(null);
  const [assignedCampaigns, setAssignedCampaigns] = useState([]);
  const [phishingRecipients, setPhishingRecipients] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    async function load() {
      setLoading(true);
      
      const [empRes, assnRes, phishRes, campRes, simRes, modRes] = await Promise.all([
        dataFetch(`read/employees?id=${id}`),
        dataFetch(`read/assignments?employee_id=${id}&limit=5000`),
        dataFetch(`read/phishing_recipients?employee_id=${id}&limit=5000`),
        dataFetch(`read/campaigns?limit=5000`),
        dataFetch(`read/phishing_simulations?limit=5000`),
        dataFetch(`read/modules?limit=5000`)
      ]);

      if (empRes?.status === 'success') {
        const foundEmp = (empRes.data || []).find(e => String(e.id) === String(id));
        
        if (foundEmp) {
          const emp = { ...foundEmp };
          
          const userAssn = (assnRes?.data || []).filter(a => String(a.employee_id) === String(id));
          const userPhish = (phishRes?.data || []).filter(p => String(p.employee_id) === String(id));
          const allCamps = campRes?.data || [];
          const allMods = modRes?.data || [];
          
          // Determine which campaigns the learner is actually assigned to
          const myCamps = allCamps.filter(c => {
             const learners = getArray(c.assignedLearners).map(String);
             const isExplicit = learners.includes(String(emp.id));
             const deptArray = c.audience && c.audience !== 'Custom' ? c.audience.split(',').map(s => s.trim()) : [];
             const isDept = emp.department && deptArray.includes(emp.department);
             return isExplicit || isDept;
          });

          // Compute progress for each assigned campaign based on module assignments
          const displayCamps = myCamps.map(c => {
            const cModIds = getArray(c.trainingContent).map(String);
            const cMods = allMods.filter(m => cModIds.includes(String(m.id)));
            
            const modules = cMods.map(m => {
              const assignment = userAssn.find(a => String(a.campaign_id) === String(c.id) && String(a.module_id) === String(m.id));
              const expiresAt = assignment?.expires_at ? new Date(assignment.expires_at) : null;
              const isExpired = expiresAt && expiresAt < new Date();
              
              return {
                id: m.id,
                title: m.title,
                status: assignment ? assignment.status : 'Not Started',
                score: assignment ? assignment.score : null,
                attempts: assignment ? (assignment.attempts || 0) : 0,
                time_spent: assignment ? (assignment.time_spent || 0) : 0,
                expires_at: assignment?.expires_at,
                isExpired
              };
            });

            // Recalculate progress dropping expired modules
            const completedCount = modules.filter(m => m.status === 'Completed' && !m.isExpired).length;
            const pct = cMods.length ? Math.round((completedCount / cMods.length) * 100) : 0;
            
            let status = pct === 100 ? 'Completed' : pct > 0 ? 'In Progress' : 'Not Started';
            if (new Date(c.endDate) < new Date() && status !== 'Completed') {
               status = 'Overdue';
            }

            return {
              id: c.id,
              campaignName: c.campaignName,
              startDate: c.startDate,
              endDate: c.endDate,
              pct,
              status,
              modules
            };
          });

          const totalCamps = displayCamps.length;
          const compCamps = displayCamps.filter(c => c.status === 'Completed').length;
          emp.trainingPct = totalCamps > 0 ? Math.round((compCamps / totalCamps) * 100) : 0;
          
          let compStatus = 'Not Started';
          if (totalCamps > 0) {
            let isOverdue = displayCamps.some(c => c.status === 'Overdue');
            let isAllComp = displayCamps.every(c => c.status === 'Completed');
            if (isOverdue) compStatus = 'Overdue';
            else if (isAllComp) compStatus = 'Compliant';
            else compStatus = 'In Progress';
          }
          emp.compStatus = compStatus;

          const totalP = userPhish.length;
          const clickedP = userPhish.filter(p => p.status === 'Clicked').length;
          emp.phishingRiskScore = totalP > 0 ? Math.round((clickedP / totalP) * 100) : 0;

          setLearner(emp);
          setAssignedCampaigns(displayCamps);
          setPhishingRecipients(userPhish);
          setSimulations(simRes?.data || []);
        } else {
          setLearner(null);
        }
      }
      setLoading(false);
    }
    load();
  }, [id, refreshTrigger]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 text-slate-500">
      <SafeIcon icon={FiLoader} className="animate-spin text-3xl mb-4" />
      <p className="text-lg font-semibold">Loading learner data...</p>
    </div>
  );

  if (!learner) return (
    <div className="flex flex-col items-center justify-center h-64 text-slate-500">
      <p className="text-lg font-semibold">Learner not found</p>
      <button onClick={() => navigate('/learners')} className="mt-4 text-blue-600 hover:underline text-sm">← Back to Learners</button>
    </div>
  );

  const learnerFullName = `${learner.firstName || ''} ${learner.lastName || ''}`.trim();

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-[1400px] mx-auto">
      <button onClick={() => navigate('/learners')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors font-medium w-fit">
        <SafeIcon icon={FiArrowLeft} /> Back to Learners
      </button>

      <ProfileHeader learner={learner} />
      <ProfileActions learner={learner} onUpdate={() => setRefreshTrigger(t => t + 1)} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <TrainingSection assignedCampaigns={assignedCampaigns} learnerName={learnerFullName} />
          <PhishingHistory learnerId={learner.id} recipients={phishingRecipients} simulations={simulations} />
          <ComplianceSection assignedCampaigns={assignedCampaigns} />
        </div>
        <div className="space-y-6">
          <RiskScoreChart learner={learner} />
          <AdminNotes learnerId={learner.id} />
        </div>
      </div>
    </motion.div>
  );
};

export default LearnerProfile;